import { Fragment, isValidElement, ReactElement } from 'react';
import {
    MdOutlineDashboard,
    MdOutlineDescription,
    MdOutlineHome,
    MdOutlineInsertChart,
    MdSettings,
    MdWorkOutline,
} from 'react-icons/md';
import { ReactSVG } from 'react-svg';
import { ITreeOption } from '@/types';
import { FoldersService } from '@services/FoldersService';
import { GRID_STATE_PARAM } from '@organisms/DataGrid/dataGridUrlState';
import { getSystemFolderIcon } from './systemFolderIcons';
import { MdDictionary } from '@icons';
import logo from '@brand/logo.svg';

export type NodeKey = string;

const IMAGE_EXTENSIONS = new Set(['png', 'svg', 'jpg']);

const SYSTEM_ICON_FACTORIES: Record<string, () => ReactElement> = {
    home: () => <MdOutlineHome size="24" />,
    tasks: () => <MdWorkOutline size="24" />,
    docs: () => <MdOutlineDescription size="24" />,
    dicts: () => <MdDictionary size="24" />,
    reports: () => <MdOutlineInsertChart size="24" />,
    dashboards: () => <MdOutlineDashboard size="24" />,
};

const PRIMARY_MENU_ICONS: Record<string, string> = {
    'tasks/tasks': 'tasks',
    'documents/documents': 'docs',
    'reports/reports': 'reports',
    'dictionaries/dictionaries': 'dicts',
};

const isFileIcon = (iconName: string): boolean => {
    const extension = iconName.split('.').at(-1)?.trim().toLowerCase();
    return Boolean(extension && IMAGE_EXTENSIONS.has(extension));
};

const getFileIconPath = (iconName: string): string => {
    const logoName = logo.substring(logo.indexOf('logo'));
    return logo.replace(logoName, iconName);
};

export const getIcon = (icon: string): ReactElement | null => {
    const normalized = icon.trim().toLowerCase();

    if (isFileIcon(normalized)) {
        const path = getFileIconPath(normalized);
        if (normalized.endsWith('.svg')) {
            return <ReactSVG src={path} />;
        }

        return <img src={path} alt="icon" />;
    }

    return SYSTEM_ICON_FACTORIES[normalized]?.() ?? null;
};

export const withPreparedRootIcon = (item: ITreeOption): ITreeOption => {
    if (typeof item.icon !== 'string') {
        return item;
    }

    const primaryIcon =
        PRIMARY_MENU_ICONS[
            String(item.value ?? '')
                .trim()
                .toLowerCase()
        ];
    const usePrimaryIcon = primaryIcon === item.icon.trim().toLowerCase();

    return {
        ...item,
        icon: usePrimaryIcon ? getSystemFolderIcon(primaryIcon) : getIcon(item.icon),
    };
};

/** Окно исполнителя действия. Исполнители без собственного окна (навигация, скачивание)
 *  возвращают пустой фрагмент — он не должен считаться открытым popup и держать панель. */
export const executorModalElement = (visualElement?: () => ReactElement): ReactElement | undefined => {
    const element = visualElement?.();
    if (!element || !isValidElement(element)) return undefined;
    const props = element.props as { children?: unknown };
    return element.type === Fragment && props.children == null ? undefined : element;
};

export const getAdminRootNode = (): ITreeOption => ({
    value: 'administrations',
    label: 'Администрирование',
    groupId: 'system',
    hasChildren: true,
    checked: false,
    icon: <MdSettings size="24" />,
    loading: false,
    children: [
        {
            value: 'addressBook',
            label: 'Условные группы',
            checked: false,
            hasChildren: false,
            url: '/administrations/dyngroups',
        },
    ],
    url: '/administrations',
});

const fetchMenuItems = async (node: ITreeOption): Promise<ITreeOption[]> => {
    if (!node.hasChildren || (node.children && node.children.length > 0) || !node.value) {
        return [];
    }

    const [category, submenu] = node.value.split('/').map((x: string) => x.trim().toLowerCase());

    if (!category || !submenu) {
        return [];
    }

    switch (category) {
        case 'tasks': {
            const response = await FoldersService.tasks(submenu);
            return response.data;
        }
        case 'documents': {
            const response = await FoldersService.documents(submenu);
            return response.data;
        }
        case 'dictionaries': {
            const response = await FoldersService.dictionaries(submenu);
            return response.data;
        }
        case 'reports': {
            const response = await FoldersService.reports(submenu);
            return response.data;
        }
        case 'dashboards': {
            const response = await FoldersService.dashboards(submenu);
            return response.data;
        }
        default:
            return [];
    }
};

export const getNodeKey = (parentKey: string, itemValue: ITreeOption['value']): NodeKey => {
    const value = String(itemValue ?? '');
    return parentKey ? `${parentKey}::${value}` : value;
};

const getNodeDiscriminator = (item: ITreeOption): string => {
    const label = typeof item.label === 'string' || typeof item.label === 'number' ? String(item.label) : '';
    return encodeURIComponent(JSON.stringify([item.url ?? '', label]));
};

/**
 * Ключи ОДНОГО уровня дерева. Считаются пачкой и только по полному составу уровня: дубликаты
 * `value` получают дискриминатор, поэтому ключ узла зависит от соседей. Значит, ключ нельзя
 * получать для узла в отрыве от его уровня (массивом из одного элемента) — он разойдётся с
 * ключом того же узла в renderTree/replaceNodeChildren. Готовый ключ пробрасываем аргументом
 * (см. collectUnloadedNodesForNode).
 */
export const getNodeKeys = (items: ITreeOption[], parentKey = ''): NodeKey[] => {
    const baseKeys = items.map((item) => getNodeKey(parentKey, item.value));
    const baseKeyCounts = new Map<NodeKey, number>();

    baseKeys.forEach((key) => baseKeyCounts.set(key, (baseKeyCounts.get(key) ?? 0) + 1));

    const usedKeys = new Set<NodeKey>();

    return items.map((item, index) => {
        const baseKey = baseKeys[index];
        const candidate = baseKeyCounts.get(baseKey) === 1 ? baseKey : `${baseKey}::@${getNodeDiscriminator(item)}`;
        let nodeKey = candidate;
        let occurrence = 1;

        while (usedKeys.has(nodeKey)) {
            occurrence += 1;
            nodeKey = `${candidate}::#${occurrence}`;
        }

        usedKeys.add(nodeKey);
        return nodeKey;
    });
};

export const shouldLazyLoadNode = (item: ITreeOption): boolean => {
    return item.hasChildren === undefined
        ? !item.children || item.children.length === 0
        : item.hasChildren && !item.children?.length;
};

export const hasNestedItems = (item: ITreeOption): boolean => {
    return Boolean(item.hasChildren || item.children?.length);
};

/**
 * Подставляет детей сразу нескольким узлам за один обход дерева. Ветки без подстановок остаются теми же объектами
 * (без них — тот же массив): по ним меню не перерисовывает неизменённые пункты.
 */
export const replaceNodesChildren = (
    items: ITreeOption[],
    replacements: ReadonlyMap<NodeKey, ITreeOption[]>,
    parentKey = '',
): ITreeOption[] => {
    if (replacements.size === 0) {
        return items;
    }
    const nodeKeys = getNodeKeys(items, parentKey);
    let changed = false;

    const next = items.map((item, index) => {
        const nodeKey = nodeKeys[index];
        const children = replacements.get(nodeKey);

        if (children) {
            changed = true;
            return { ...item, children };
        }

        if (!item.children?.length) {
            return item;
        }

        const nested = replaceNodesChildren(item.children, replacements, nodeKey);
        if (nested === item.children) {
            return item;
        }
        changed = true;
        return { ...item, children: nested };
    });

    return changed ? next : items;
};

export const replaceNodeChildren = (
    items: ITreeOption[],
    targetKey: NodeKey,
    children: ITreeOption[],
    parentKey = '',
): ITreeOption[] => replaceNodesChildren(items, new Map([[targetKey, children]]), parentKey);

/** Адрес для сопоставления с url пунктов меню: без транзитных параметров.
 *  `gridState` хранит состояние грида и приезжает в location при возврате
 *  назад — пункт папки от него не зависит и не должен терять подсветку. */
/** Канонический адрес пункта меню: одинаковая кодировка и порядок параметров с обеих
 *  сторон сравнения. Бэк кодирует значения через UrlPathEncode (`%20`, кириллица как
 *  `%D0…`), а URLSearchParams браузера — иначе (`+`), поэтому буквальное равенство
 *  строк рвётся на справочниках и отчётах с русскими именами. */
export const canonicalMenuUrl = (url: string): string => {
    const queryStart = url.indexOf('?');
    const pathname = queryStart < 0 ? url : url.slice(0, queryStart);
    const params = new URLSearchParams(queryStart < 0 ? '' : url.slice(queryStart + 1));
    params.delete(GRID_STATE_PARAM);
    if (pathname === '/reports') {
        const subMenu = params.get('subMenu')?.toLowerCase();
        if (!subMenu || subMenu === 'reports') params.delete('subMenu');
        else params.set('subMenu', subMenu);
    }
    params.sort();
    const query = params.toString();
    return `${pathname}${query ? `?${query}` : ''}`;
};

export const menuLocationPath = (pathname: string, search: string): string => canonicalMenuUrl(`${pathname}${search}`);

export const findNodePathByUrl = (items: ITreeOption[], path: string, parentKey = ''): NodeKey[] | undefined => {
    const nodeKeys = getNodeKeys(items, parentKey);

    for (const [index, item] of items.entries()) {
        const nodeKey = nodeKeys[index];

        if (item.url && canonicalMenuUrl(item.url) === path) {
            return [nodeKey];
        }

        if (item.children?.length) {
            const childPath = findNodePathByUrl(item.children, path, nodeKey);
            if (childPath) {
                return [nodeKey, ...childPath];
            }
        }
    }

    return undefined;
};

/** Пункт меню со счётчиком: `counter` из XML, ключ запроса и режим не `off`. */
export const hasFolderCounter = (item: ITreeOption): item is ITreeOption & { countKey: string } =>
    item.counter === true && !!item.countKey && item.counterMode !== 'off';

export const parseSubMenuFromUrl = (path: string): string | null => {
    const queryStart = path.indexOf('?');
    const params = new URLSearchParams(queryStart < 0 ? '' : path.slice(queryStart + 1));
    const submenu = params.get('subMenu');
    if (submenu) {
        return submenu;
    }

    // GUID-ссылки дашбордов и поисковые папки отчётов не содержат subMenu,
    // но ветку нужно загрузить до поиска активного пункта.
    const pathname = queryStart < 0 ? path : path.slice(0, queryStart);
    const section = ['dashboards', 'reports', 'dictionaries', 'documents', 'tasks'].find(
        (name) => pathname === `/${name}` || pathname.startsWith(`/${name}/`),
    );
    return section ?? null;
};

export interface IUnloadedNode {
    node: ITreeOption;
    key: NodeKey;
}

export const collectUnloadedNodes = (items: ITreeOption[], parentKey = ''): IUnloadedNode[] => {
    const result: IUnloadedNode[] = [];
    const nodeKeys = getNodeKeys(items, parentKey);

    for (const [index, item] of items.entries()) {
        const nodeKey = nodeKeys[index];

        if (shouldLazyLoadNode(item)) {
            result.push({ node: item, key: nodeKey });
        } else if (item.children?.length) {
            result.push(...collectUnloadedNodes(item.children, nodeKey));
        }
    }

    return result;
};

/**
 * То же для поддерева одного узла: ключ самого узла берём готовым (посчитанным по его уровню),
 * а не выводим из массива [node] — иначе у дубликатов потеряется дискриминатор и ключи детей
 * разойдутся с теми, по которым рендерится дерево и работает replaceNodeChildren.
 */
export const collectUnloadedNodesForNode = (node: ITreeOption, nodeKey: NodeKey): IUnloadedNode[] => {
    if (shouldLazyLoadNode(node)) {
        return [{ node, key: nodeKey }];
    }

    return node.children?.length ? collectUnloadedNodes(node.children, nodeKey) : [];
};

const pendingMenuRequests = new Map<string, Promise<ITreeOption[]>>();

export const lazyLoadItems = (node: ITreeOption): Promise<ITreeOption[]> => {
    if (!node.hasChildren || node.children?.length || !node.value) return Promise.resolve([]);
    const key = String(node.value).toLowerCase();
    const pending = pendingMenuRequests.get(key);
    if (pending) return pending;
    const request = fetchMenuItems(node).finally(() => pendingMenuRequests.delete(key));
    pendingMenuRequests.set(key, request);
    return request;
};
